import { createContext, useContext, useState, useMemo, type ReactNode } from 'react';
import { useMultiplayer } from '../hooks/useMultiplayer';
import { useAuth } from '../hooks/useAuth';
import { usePages } from '../hooks/usePages';
import type { SessionSettings, GameSystemId } from '@roll1/shared';

type UseMultiplayerReturn = ReturnType<typeof useMultiplayer>;
type UseAuthReturn = ReturnType<typeof useAuth>;
type UsePagesReturn = ReturnType<typeof usePages>;

export interface SessionContextValue {
  auth: UseAuthReturn;
  mp: UseMultiplayerReturn;
  pages: UsePagesReturn;
  isGM: boolean;
  outgoingPageId: string;
  gameSystem: GameSystemId;
  sessionSettings: SessionSettings;
  setSessionSettings: React.Dispatch<React.SetStateAction<SessionSettings>>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const mp = useMultiplayer();
  const pages = usePages();
  const [sessionSettings, setSessionSettings] = useState<SessionSettings>({
    measurementMode: 'simple',
    gridSizeFt: 5,
    playerAiEnabled: true,
    playerAiTokenBudgetPerHour: null,
    playerAiRateLimitPerMinute: 10,
    aiAssistantName: 'Tiah',
    aiMentionEnabled: true,
    aiMentionRateLimitPerMinute: 5,
    imageGenModel: 'flux-schnell',
    imageGenStylePrefix: '',
    imageGenRateLimitPerHour: 10,
    // v31: Display
    gridStyle: 'lines',
    gridOpacity: 100,
    defaultTokenSize: 'medium',
    hpBarVisibility: 'hover',
    // v32: Combat rules
    autoRollInitiative: false,
    confirmDamage: false,
    dnd5e: { critRange: 20, deathSavesEnabled: true },
    // v33: Permissions
    playerPermissions: {
      moveOtherTokens: false,
      drawOnMap: true,
      viewOtherSheets: 'basic',
      createNotes: true,
      useAi: true,
      addTokens: false,
    },
    // v34: Ambient overlay
    ambientOverlayEnabled: true,
    // v37: Custom stickers
    allowPlayerStickers: false,
  });

  const isGM = !mp.session || mp.participant?.role === 'gm';
  const outgoingPageId = pages.viewingPageId;
  const gameSystem: GameSystemId = mp.serverState?.gameSystem ?? 'dnd5e';

  const value = useMemo(
    () => ({ auth, mp, pages, isGM, outgoingPageId, gameSystem, sessionSettings, setSessionSettings }),
    [auth, mp, pages, isGM, outgoingPageId, gameSystem, sessionSettings, setSessionSettings],
  );

  return (
    <SessionContext.Provider value={value}>
      {children}
    </SessionContext.Provider>
  );
}

export function useSessionContext() {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSessionContext must be used within SessionProvider');
  return ctx;
}

/**
 * Non-throwing variant for components that ALSO render outside the session
 * tree (demo mode, unit tests render sections without providers). Returns
 * null there; callers degrade to their providerless behavior.
 */
export function useOptionalSessionContext(): SessionContextValue | null {
  return useContext(SessionContext);
}
